import {AtlasConfig, Locale} from "./Models/AtlasConfig";
import Lottie, {ExtendedAnimationItem} from "lottie-web";
import {DotNetObjectReference} from "@App/BlazorTypes";
import {LoadingMessages} from "@App/Models/LoadingMessages";
import {GlobalErrorMessages, GlobalErrors} from "@App/Models/GlobalErrorMessages";
import {SiteKeyAndPageKey} from "@App/Models/SiteKeyAndPageKey";

import 'KortInfo.Atlas.UI.Blazor/wwwroot/styles/styles.scss'
import 'KortInfo.Atlas.UI.Blazor.Controls/wwwroot/styles/styles.scss'
import 'KortInfo.Atlas.UI.Blazor.Map.OpenLayers/wwwroot/styles/styles.scss'
import 'KortInfo.Atlas.UI.Blazor.Map.OpenLayers/wwwroot/styles/_OpenLayers.scss'



let Atlas = (function () {

    let initialized = false;
    let _jsInteropService: DotNetObjectReference | null = null;

    let bootstrapAnimation = (function () {
        let animationPath: string | null = null;

        // Bootstrap animation runs twice. One animation before blazor is loaded and another after the animation is loader.
        // The animation MUST have markers ranging from "Marker_01" at frame 0 and "Marker_08" at the last frame
        let _currentBoostrapAnimation: ExtendedAnimationItem | null = null;

        let beginBeforeBlazorAnimation = async (atlasConfig: AtlasConfig, containerId: string) => {
            animationPath = atlasConfig.atlasBaseUrl + 'animations/magnets.json';
            const englishLocale: Locale = 'en'; 
            const loadingMessages: LoadingMessages = {
                da: {
                    Downloading: 'Downloader filer: '
                },
                en: {
                    Downloading: 'Downloading files: '
                }
            }

            _currentBoostrapAnimation = await createLottieAnimation(containerId);

            playAtlasBootstrapAnimation('Marker_01', 'Marker_02');

            // Create global methods to make them easily accessible from blazor
            window.__playAtlasBootstrapAnimation = playAtlasBootstrapAnimation;
            window.__transitionToAtlasBlazorAnimation = transitionToAtlasBlazorAnimation;
            window.__afterAtlasBootstrapAnimation = afterBootstrapAnimation;

            let progressElement = document.getElementById('loading-progress-text')
                || document.querySelector('.loading-progress-text');

            const progressInterval = setInterval(() => {
                let percentText = getComputedStyle(document.documentElement).getPropertyValue('--blazor-load-percentage').trim();
                let num = parseInt(percentText, 10);

                if (isNaN(num)) num = 0;
                num = Math.min(100, Math.max(0, num));

                if (progressElement) {
                    progressElement.innerText = `${loadingMessages[atlasConfig.defaultLocale].Downloading ?? loadingMessages[englishLocale].Downloading}  ${num}%`;
                }

                if (num >= 100) {
                    clearInterval(progressInterval);
                }
            }, 100);

            // Listen for blazor load changed number
            let lastValueStr = getComputedStyle(document.documentElement).getPropertyValue('--blazor-load-percentage').trim();
            let lastValue = lastValueStr ? parseFloat(lastValueStr) : 0;

            const intervalId = setInterval(() => {
                const newValueStr = getComputedStyle(document.documentElement).getPropertyValue('--blazor-load-percentage').trim();
                const newValue = newValueStr ? parseFloat(newValueStr) : 0;

                if (newValue !== lastValue) {
                    lastValue = newValue;

                    if (newValue > 50) {
                        playAtlasBootstrapAnimation('Marker_02', 'Marker_03');
                        clearInterval(intervalId);
                    }
                }
            }, 100);

        }

        let transitionToAtlasBlazorAnimation = async (containerId: string) => {
            let oldBootsrapAnimation: ExtendedAnimationItem | null = _currentBoostrapAnimation;
            
            _currentBoostrapAnimation = await createLottieAnimation(containerId);
            const marker: number | null = getMarkerFrame('Marker_03');
            if(_currentBoostrapAnimation != null && marker != null) {
                _currentBoostrapAnimation.goToAndStop( marker, true);
            }
            
            if(oldBootsrapAnimation != null) {
                oldBootsrapAnimation.destroy();
            }
        }

        let afterBootstrapAnimation = function () {
            _currentBoostrapAnimation?.destroy();
            _currentBoostrapAnimation = null;

            // Clear global methods
            window.__playAtlasBootstrapAnimation = null;
            window.__transitionToAtlasBlazorAnimation = null;
            window.__afterAtlasBootstrapAnimation = null;
        }

        let playAtlasBootstrapAnimation = (markerFrom: string, markerTo: string) => {
            const from = getMarkerFrame(markerFrom);
            const to = getMarkerFrame(markerTo);
            if (from !== null && to !== null && _currentBoostrapAnimation != null) {
                _currentBoostrapAnimation.playSegments([from, to], true);
            }
        }

        function createLottieAnimation(containerId: string): Promise<ExtendedAnimationItem | null> {
            return new Promise((resolve) => {
                if (!animationPath) {
                    console.warn('Failed to create animation. Animation path was not set.');
                    resolve(null);
                    return;
                }
                
                const containerElement: null | HTMLElement = document.getElementById(containerId);
                if(containerElement === null)
                    return;

                const boostrapAnimation: ExtendedAnimationItem = Lottie.loadAnimation({
                    container: containerElement,
                    renderer: 'svg',
                    loop: false,
                    autoplay: false,
                    path: animationPath
                });

                const waitForAnimation = () => {
                    if (boostrapAnimation.isLoaded) {
                        resolve(boostrapAnimation);
                    } else {
                        setTimeout(waitForAnimation, 20);
                    }
                };

                waitForAnimation();
            });
        }

        function getMarkerFrame(name: string): number | null {
            const marker = _currentBoostrapAnimation?.markers?.find(m => m?.payload?.name === name);

            if (!marker) {
                console.warn(`Could not find bootstrap animation marker '${name}'`);
                return null;
            }

            return marker.time;
        }

        return {
            beginBeforeBlazorAnimation: beginBeforeBlazorAnimation
        };

    })();

    let _loadScript = function (rootUrl: string, relativeUrl: string, attributes?: Record<string, string>[]) {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            if (!rootUrl.endsWith('/'))
                rootUrl = rootUrl + '/';
            const url = rootUrl + relativeUrl;
            script.src = url;
            script.type = 'text/javascript';
            if (!!attributes) {
                for (const attribute of attributes) {
                    const key = Object.keys(attribute)[0];
                    const value = attribute[key];
                    script.setAttribute(key, value);
                }
            }
            script.onload = () => {
                resolve(`Script loaded: ${url}`);
            };
            script.onerror = () => {
                reject(new Error(`Failed to load script: ${url}`));
            };
            document.body.appendChild(script);
        });
    }

    let _loadCSS = function (url: string) {
        return new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.href = url;
            link.rel = 'stylesheet';

            link.onload = () => {
                resolve(`CSS loaded: ${url}`);
            };
            link.onerror = () => {
                reject(new Error(`Failed to load CSS: ${url}`));
            };
            document.head.appendChild(link);
        });
    }

    let _loadCSSWithRelativeUrl = function (rootUrl: string, relativeUrl: string) {
        if (!rootUrl.endsWith('/'))
            rootUrl = rootUrl + '/';
        const url = rootUrl + relativeUrl;

        return _loadCSS(url)
    }

    let _loadLocalStorageTheme = function (): Promise<void> {
        return new Promise((resolve, reject) => {
            // Must match the key SettingsKeySerializer builds for ApplicationSettingsGlobalPerDevice
            const storedValue: string | undefined = localStorage.getItem("Atlas_G_ApplicationSettingsGlobalPerDevice") ?? undefined;
            if (!storedValue) {
                resolve();
                return;
            }

            try {
                const parsed = JSON.parse(storedValue!);
                const colorScheme = parsed?.ColorScheme;

                if (colorScheme === 0) {
                    // system - do nothing
                } else if (colorScheme === 1) {
                    // light - force it
                    document.documentElement.setAttribute('data-ki-theme', 'light');
                } else if (colorScheme === 2) {
                    // dark - force it
                    document.documentElement.setAttribute('data-ki-theme', 'dark');
                }
            } catch (error) {
            }

            resolve();
        });
    }

    let _loadAppCSS = function (backendUrl: string, siteKey: string, pageKey: string) {
        // App-layout.css is handled in a special function since it's constructed dynamically based on the 
        // current thematics of the page. Therefore, we need to keep the /<site>/<page>/ parts of the url.
        let pathPart = window.location.pathname;
        if (!pathPart.endsWith('/')) {
            pathPart += '/';
        }

        if (pathPart.startsWith('/')) {
            pathPart = pathPart.substring(1);
        }

        if (!pathPart.toLowerCase().startsWith('atlas/')) {
            pathPart = 'atlas/' + pathPart;
        }
        
        if (pathPart.endsWith('atlas-pub/')) {
            // Remove atlas-pub from query string, so we end up with <host>/<virtualdir>/atlas/
            pathPart = pathPart.substring(0, pathPart.length - 'atlas-pub/'.length);
        }

        let appCssUrl = `${backendUrl}atlas/${siteKey}/${pageKey}/app-layout.css`;

        return _loadCSS(appCssUrl);
    }

    let _addBlazorComponent = function (componentName: string, container: string) : Promise<void> {
        return new Promise((resolve, reject) => {

            if (!initialized) {
                reject(new Error(`Atlas not initialized. Call .initialize() before adding Atlas to a container.`));
                return;
            }

            // Because of a weird loading race condition in Blazor we need to do the following retries.
            // This comment was written in .net 8
            // See: https://stackoverflow.com/questions/69319216/blazor-enable-dynamic-root-components-error-dynamic-root-components-have-not

            let attempts = 0
            const maxAttempts = 1200 // arbitrary choice

            function AddBlazorComponents() {
                if (attempts > maxAttempts) {
                    // give up after trying too many times
                    reject(new Error(`Could not load Blazor components.`));
                    return;
                }

                let targetElement = document.getElementById(container);
                Blazor.rootComponents.add(targetElement, componentName, {})
                    .then(() => {
                        resolve();
                    })
                    .catch((reason: any) => {
                        if (reason.message === "Dynamic root components have not been enabled in this application.")
                            requestAnimationFrame(AddBlazorComponents)
                        else
                            reject(new Error(`Could not load Blazor components.: ${reason.message}`));
                    });
                attempts++
            }

            AddBlazorComponents();
        });
    };
    
    let ensureSiteAndPageInfoInAtlasConfig = async function (atlasConfig: AtlasConfig): Promise<void> {
        if (atlasConfig.siteKey && atlasConfig.pageKey && atlasConfig.siteId) {
            return;
        } 
        
        if (atlasConfig.siteKey && !atlasConfig.siteId) {
            // Resolve from the server - this only ever really happens on local debug
            try {
                const url = atlasConfig.backendUrl + `atlas-api/siteinfo/${atlasConfig.siteKey}`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Conent-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Status: ${response.status}, ${response.statusText}`);
                }

                const resultObject = await response.json();

                if (!resultObject.siteId) {
                    throw new Error(`Could not resolve siteId from server. Server response: ${JSON.stringify(resultObject)}`);
                }

                atlasConfig.siteId = resultObject.siteId;
            }
            catch (error: any) {
                throw new Error(`Failed to resolve siteId using siteKey: ${atlasConfig.siteKey}. Error: ${error.message}`, error);
            }
        }
        
        if (atlasConfig.siteId && atlasConfig.snapshotIdentifier) {
            // Resolve from the server
            try {
                const url = atlasConfig.backendUrl + `atlas-api/snapshots/sitekeyandpagekey/${atlasConfig.siteId}/${atlasConfig.snapshotIdentifier}`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Conent-Type': 'application/json',
                    },
                });
                
                if (!response.ok) {
                    throw new Error(`Status: ${response.status}, ${response.statusText}`);
                }
                
                const resultObject = await response.json();
                
                if (!resultObject.siteKey || !resultObject.pageKey) {
                    throw new Error(`Could not resolve siteKey and pageKey from server. Server response: ${JSON.stringify(resultObject)}`);
                }
                
                atlasConfig.siteKey = resultObject.siteKey;
                atlasConfig.pageKey = resultObject.pageKey;
            }
            catch (error: any) {
                throw new Error(`Failed to resolve siteKey and pageKey using siteId: ${atlasConfig.siteId} and snapshot identifier: ${atlasConfig.snapshotIdentifier}. Error: ${error.message}`, error);
            }
        }
        
        if (!atlasConfig.siteKey || !atlasConfig.pageKey || !atlasConfig.siteId) {
            throw new Error('Could not needed startup information. Please provide either siteKey and pageKey or siteId and snapshotIdentifier.');
        }
    }

    let initialize = async function (atlasConfig: AtlasConfig) {
        window.__getAtlasConfig = () => {
            return atlasConfig;
        };

        window.atlas_registerService = function (jsInteropService) {
            _jsInteropService = jsInteropService;
        }

        // Resolve sitekey + pagekey if needed
        await ensureSiteAndPageInfoInAtlasConfig(atlasConfig);
        
        // Attach JS listeners
        window.addEventListener('message', function (event) {
            if (event.data.action === 'openLayersLibrary') {
                openLayersLibrary();
            } else if (event.data.action === 'openActiveLayers') {
                openActiveLayers();
            }
        });

        _setGlobalErrorMessages(atlasConfig.defaultLocale);

        let _rootAtlasUrlTemp;
        if (atlasConfig.atlasBaseUrl.endsWith('/')) {
            _rootAtlasUrlTemp = atlasConfig.atlasBaseUrl.substring(0, atlasConfig.atlasBaseUrl.length - 1);
        } else {
            _rootAtlasUrlTemp = atlasConfig.atlasBaseUrl;
        }

        const rootAtlasUrl = _rootAtlasUrlTemp;
        const backendUrl = atlasConfig.backendUrl;

        //Indlejret Atlas (KIF-16467): broen til vÃ¦rtens SDK skal lytte fra start, sÃ¥ vÃ¦rten ikke venter forgÃ¦ves.
        if (atlasConfig.mode === 'embed') {
            void import(/* @vite-ignore */ `${rootAtlasUrl}/js/kortinfo-map-atlas.module.js`);
        }

        await _loadScript(rootAtlasUrl, '_framework/blazor.webassembly.js', [
            {
                "autostart": "false"
            }]
        );

        let loadBootResource = function (type: string, name: string, defaultUri: string, integrity: string) {
            switch (type) {
                case 'dotnetjs':
                case 'dotnetwasm':
                case 'timezonedata':
                    if (defaultUri.startsWith('http')) {
                        return null;
                    }

                    let result = `${rootAtlasUrl}/${defaultUri}`;
                    //console.log(`Loading: '${type}', '${name}', '${defaultUri}', '${integrity}': ${result}`); 
                    return result;
            }
        };

        const essentialTasks = Promise.allSettled([
            // App CSS to get bootstrapping styling asap
            _loadAppCSS(backendUrl, atlasConfig.siteKey!, atlasConfig.pageKey!),
            _loadLocalStorageTheme(),
        ]);

        /*const additionalResources = await fetchBootloaderAdditions();

        const dynamicTasks = additionalResources.map(ref => {
            const {resourceName, resourcePath, resourceType } = ref;

            if (resourceType === 0) {
                console.log("Loading CSS for " + resourceName + " in " + resourcePath);
                return _loadCSSWithRelativeUrl(rootAtlasUrl, resourcePath);
            } else if (resourceType === 1) {
                console.log("Loading JS for " + resourceName + " in " + resourcePath);
                return _loadScript(rootAtlasUrl, resourcePath);
            }

            console.warn(`Unknown reference type: ${resourceType}`, ref);
            return Promise.resolve();
        });*/

        const restTasks = Promise.allSettled([
            Blazor.start({
                loadBootResource: loadBootResource,
            }),

            // Browser host helpers
            // _loadScript(rootAtlasUrl, 'js/atlas-browserhost-helpers.js'),

            // MudBlazor
            _loadCSSWithRelativeUrl(rootAtlasUrl, 'css/fonts.css'),
            _loadCSSWithRelativeUrl(rootAtlasUrl, '_content/MudBlazor/MudBlazor.min.css'),
            _loadScript(rootAtlasUrl, '_content/MudBlazor/MudBlazor.min.js'),

            // Atlas styles
            _loadCSSWithRelativeUrl(rootAtlasUrl, 'KortInfo.Atlas.Web.styles.css'),
            _loadCSSWithRelativeUrl(rootAtlasUrl, 'css/atlas-core.css'),
            _loadCSSWithRelativeUrl(rootAtlasUrl, '_content/KortInfo.Atlas.UI.Blazor.Map.OpenLayers/OL_v10_3_1/ol.css'),

            // Dynamic additions from backend
            //...dynamicTasks

            // LocalStorage
            //_loadScript(rootAtlasUrl, 'js/atlas-localstorage.js'),
        ]);

        essentialTasks.then(async () => {
            // Show loading screen once blazor is starting and we got app.css
            const loadingElement = document.getElementById('loading-screen-container');
            
            if(loadingElement !== null) {
                loadingElement.style.display = "";
            }
            
            await bootstrapAnimation.beginBeforeBlazorAnimation(atlasConfig, 'loading-screen-animation');
        });

        const loadResults = await Promise.allSettled([
            essentialTasks,
            restTasks,
        ]);

        for (const loadResult of loadResults) {
            if (loadResult.status !== 'fulfilled') {
                throw new Error(`Error loading resource: ${loadResult.reason}`);
            }
        }

        //window.__getAtlasConfig = undefined; // TODO AKKI KIF-1538 <-- Race condition halløj

        initialized = true;
    }

    let _setGlobalErrorMessages = function (locale: Locale) {
        let messages: GlobalErrors = {
            da: {
                'title': 'Åååh nej... Der skete en uhåndteret fejl.',
                'options-description': 'Du har nu følgende muligheder:',
                'options-list_reload': 'Du kan genindlæse siden (Anbefalet)',
                'options-list_continue': 'Du kan lukke dette vindue og forsøge at fortsætte (BEMÆRK: Klienten kan være ustabil eller ikke-fungerende)',
                'options-list_clearall': 'Du kan rydde alle lokale gemte data og genindlæse siden',
                'button_reload': 'Genindlæs siden (Anbefalet)',
                'button_continue': 'Luk dette vindue og fortsæt',
                'button_clearall': 'Slet alle lokale data og genindlæs',
            },
            en: {
                'title': 'Ooh no... An unhandled error occurred',
                'options-description': 'You now have the following options:',
                'options-list_reload': 'You can reload the page (Recommended)',
                'options-list_continue': 'You can close this window and try to continue (NOTE: The application might be unstable or non-functional)',
                'options-list_clearall': 'You can clear all locally stored data and reload the page',
                'button_reload': 'Reload the page (Recommended)',
                'button_continue': 'Close this window and continue',
                'button_clearall': 'Clear all locally stored data and reload',
            }
        }

        let updateUi = function (messages: GlobalErrorMessages) {
            try {
                document.getElementById('atlas_error_title')!.innerHTML = messages['title'];
                document.getElementById('atlas_error_options_description')!.innerHTML = messages['options-description'];
                document.getElementById('atlas_error_options_description_reload')!.innerHTML = messages['options-list_reload'];
                document.getElementById('atlas_error_options_description_continue')!.innerHTML = messages['options-list_continue'];
                document.getElementById('atlas_error_options_description_clearall')!.innerHTML = messages['options-list_clearall'];
                document.getElementById('atlas_error_button_reload')!.innerHTML = messages['button_reload'];
                document.getElementById('atlas_error_button_continue')!.innerHTML = messages['button_continue'];
                document.getElementById('atlas_error_button_clearall')!.innerHTML = messages['button_clearall'];
            } catch (error) {
                console.warn("Unable to set i18n strings on crash-screen");
                console.error(error);
            }
        }

        let selectedMessages;
        if (!!locale) {
            selectedMessages = messages.en;
        } else if (locale === 'da') {
            selectedMessages = messages.da;
        } else {
            selectedMessages = messages.en;
        }

        updateUi(selectedMessages);
    }

    let addAtlas = async function (container: string) {
        await _addBlazorComponent('ki-atlas-root', container);
    }

    let openLayersLibrary = async function () {
        if(_jsInteropService !== null)
            await _jsInteropService.invokeMethod('OpenLayersLibrary', {});
    }

    let openActiveLayers = async function () {
        if(_jsInteropService !== null)
            await _jsInteropService.invokeMethod("ActiveLayers", {});
    }

    return {
        initialize,
        addAtlas,
        openLayersLibrary,
        openActiveLayers
    }
})();

export default Atlas